import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Delete02Icon, LicenseDraftIcon, UserAdd01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { refereeFlagCounts } from 'helpers/flagSeverity';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AddRefereeCard } from './components/AddRefereeCard/AddRefereeCard';
import { CombinedReferees } from './components/CombinedReferees/CombinedReferees';
import { NewRefereeModal } from './components/NewRefereeModal/NewRefereeModal';
import { Referee } from './components/Referee/Referee';
import { RefereeCard } from './components/RefereeCard/RefereeCard';
import { SendRefereeRequestModal } from './components/SendRefereeRequestModal/SendRefereeRequestModal';
import Alert from 'components/Alert/Alert';
import { Form } from 'components/Form/Form/Form';
import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import { getBrandUploads } from 'api/brands-slice';
import { candidatesApi, useGetCandidateFlagCountsQuery } from 'api/candidates-api';
import { useGetFormsByTypeQuery } from 'api/forms-api';

import styles from './Reference.module.scss';

const COMBINED_ID = 0;

export const Reference = ({
  candidate,
  check,
  referees = [],
  company,
  brand,
  canAccessResults = true,
  messagePopUp = () => {},
  addReferee: addRefereeAction = () => {},
  setLoading = () => {},
  updateReferee: updateRefereeAction = () => {},
  deleteReferee = () => {},
  cancelReferee = () => {},
  getPdfReport = () => {},
  getCustomPdfReport = () => {},
  getCombinedReport = () => {},
  getCombinedCustomReport = () => {},
  sendRequestEmail = () => {},
  sendReplacementRequest = () => {},
  updateCheck = () => {},
}) => {
  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: candidateForms } = useGetFormsByTypeQuery('type:candidate');
  const { data: refereeForms } = useGetFormsByTypeQuery('type:referee');
  const { data: flagCounts } = useGetCandidateFlagCountsQuery(candidate.id, {
    refetchOnMountOrArgChange: true,
  });

  const [selectedRefereeId, setSelectedRefereeId] = useState(null);
  const selectedReferee = useMemo(() => {
    if (selectedRefereeId === null) return null;
    if (selectedRefereeId === COMBINED_ID) return { id: COMBINED_ID };
    return referees.find(referee => referee.id === selectedRefereeId) ?? null;
  }, [selectedRefereeId, referees]);
  const setSelectedReferee = referee => setSelectedRefereeId(referee?.id ?? null);
  const [refereeToDelete, setRefereeToDelete] = useState(null);
  const [showRequestAlert, setShowRequestAlert] = useState(false);
  const [showRefereeRequestEmailModal, setShowRefereeRequestEmailModal] = useState(false);
  const [showNewRefereeModal, setShowNewRefereeModal] = useState(false);
  const [candidateForm, setCandidateForm] = useState(null);
  const [refereeFormId, setRefereeFormId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [refereeDocument, setRefereeDocument] = useState(null);
  const markingBypassCompleteRef = useRef(false);
  const skippingBypassRefereeRef = useRef(false);
  const [optimisticBypassSkipped, setOptimisticBypassSkipped] = useState(null);
  const checkDetailsRef = useRef(check?.details);
  checkDetailsRef.current = check?.details;
  const updateCheckRef = useRef(updateCheck);
  updateCheckRef.current = updateCheck;
  const bypassFormRef = useRef(null);

  const invalidateFlags = () =>
    dispatch(candidatesApi.util.invalidateTags(['CandidateFlags', 'CandidateReferees']));

  const addReferee = async data => {
    const referee = await addRefereeAction(data);
    invalidateFlags();
    return referee;
  };

  const updateReferee = async (id, data) => {
    await updateRefereeAction(id, data);
    invalidateFlags();
  };

  const finishBypassNominations = async (checkId, extraDetails = {}) => {
    if (markingBypassCompleteRef.current) return;
    markingBypassCompleteRef.current = true;
    try {
      await updateCheckRef.current(checkId, {
        details: {
          ...checkDetailsRef.current,
          ...extraDetails,
          bypass_nominations_complete: true,
        },
      });
    } catch {
      markingBypassCompleteRef.current = false;
    }
  };

  const isSameCandidateFormsUsed = useMemo(() => {
    return check?.details?.form_details?.every(
      form => form?.candidate === check?.details?.form_details?.[0]?.candidate,
    );
  }, [check?.details?.form_details]);

  const isSameRefereeFormsUsed = useMemo(() => {
    return check?.details?.form_details?.every(
      form => form?.referee === check?.details?.form_details?.[0]?.referee,
    );
  }, [check?.details?.form_details]);

  const userSelectedCandidateFormId = useMemo(() => {
    return check?.details?.form_details?.[0]?.candidate || null;
  }, [check?.details?.form_details]);

  const userSelectedRefereeFormId = useMemo(() => {
    return check?.details?.form_details?.[0]?.referee || null;
  }, [check?.details?.form_details]);

  const checkReferees = useMemo(
    () =>
      referees.filter(
        referee =>
          referee.candidate_check_id == check?.id && !referee.replaced_by && !referee.canceled,
      ),
    [referees, check?.id],
  );

  const totalRefereeSlots = check?.details?.referees ?? 0;
  const bypassSkippedCount = optimisticBypassSkipped ?? check?.details?.bypass_skipped ?? 0;
  const bypassNominationComplete = check?.details?.bypass_nominations_complete;
  const bypassActive = Boolean(
    check?.details?.bypassed && !bypassNominationComplete && candidateForms?.length,
  );
  const accountedBypassSlots = checkReferees.length + bypassSkippedCount;
  const remainingBypassSlots = Math.max(0, totalRefereeSlots - accountedBypassSlots);
  const showBypassForm = bypassActive && remainingBypassSlots > 0;

  const currentBypassForm = useMemo(() => {
    if (!showBypassForm) return null;

    const formDetails = check?.details?.form_details || [];
    const slotIndex = accountedBypassSlots;
    const detail = formDetails[slotIndex] || formDetails[0] || {};

    const resolveCandidateForm = candidateFormId =>
      candidateFormId == null ? null : (candidateForms.find(f => f.id == candidateFormId) ?? null);

    const defaultCandidateForm =
      formDetails.map(d => resolveCandidateForm(d.candidate)).find(Boolean) ?? candidateForms[0];

    const defaultRefereeFormId =
      formDetails.find(d => d.referee != null)?.referee ?? formDetails[0]?.referee ?? null;

    return {
      candidateForm: resolveCandidateForm(detail.candidate) ?? defaultCandidateForm,
      refereeFormId: detail.referee ?? defaultRefereeFormId,
    };
  }, [showBypassForm, check?.details?.form_details, accountedBypassSlots, candidateForms]);

  useEffect(() => {
    if (skippingBypassRefereeRef.current) return;
    const serverSkipped = check?.details?.bypass_skipped ?? 0;
    if (optimisticBypassSkipped != null && serverSkipped >= optimisticBypassSkipped) {
      setOptimisticBypassSkipped(null);
    }
  }, [check?.details?.bypass_skipped, optimisticBypassSkipped]);

  useEffect(() => {
    markingBypassCompleteRef.current = false;
    skippingBypassRefereeRef.current = false;
    setOptimisticBypassSkipped(null);
  }, [check?.id]);

  useEffect(() => {
    if (!bypassActive || remainingBypassSlots > 0) return;
    finishBypassNominations(check.id);
  }, [bypassActive, remainingBypassSlots, check?.id]);

  useEffect(() => {
    if (!showBypassForm) return;
    setCandidateForm(null);
    setRefereeFormId(null);
  }, [showBypassForm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get('addReferee') === 'true';
    if (shouldOpen) {
      setShowNewRefereeModal(true);
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!brand?.id || !brand?.documents_active) {
      setDocuments([]);
      return;
    }

    dispatch(getBrandUploads(brand.id)).then(({ payload }) => {
      setDocuments(payload.uploads);
    });
  }, [brand?.id, brand?.documents_active, dispatch]);

  const refereeDocumentId = check?.details?.referee_document_id ?? null;

  useEffect(() => {
    if (!refereeDocumentId) {
      setRefereeDocument(null);
      return;
    }

    getUpload(refereeDocumentId)
      .then(setRefereeDocument)
      .catch(() => setRefereeDocument(null));
  }, [refereeDocumentId]);

  const refereeDocuments = useMemo(() => {
    if (!refereeDocument || documents.some(document => document.id === refereeDocument.id)) {
      return documents;
    }
    return [refereeDocument, ...documents];
  }, [documents, refereeDocument]);

  const onBypassRefereeSubmit = async answers => {
    if (!currentBypassForm) return;
    setLoading(true);
    try {
      const referee = await addReferee({
        candidate_id: candidate.id,
        candidate_check_id: check.id,
        candidate_form_id: currentBypassForm.candidateForm.id,
        form_id: currentBypassForm.refereeFormId,
        manual: true,
        answers,
      });
      setSelectedReferee(referee);
      bypassFormRef.current?.reset();
      setShowRequestAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const onSkipBypassReferee = async () => {
    if (skippingBypassRefereeRef.current || markingBypassCompleteRef.current) return;
    skippingBypassRefereeRef.current = true;
    bypassFormRef.current?.reset();

    const priorSkipped = bypassSkippedCount;
    const nextSkipped = priorSkipped + 1;
    setOptimisticBypassSkipped(nextSkipped);
    const priorDetails = checkDetailsRef.current;

    try {
      const details = { ...priorDetails, bypass_skipped: nextSkipped };
      checkDetailsRef.current = details;

      if (checkReferees.length + nextSkipped >= totalRefereeSlots) {
        await finishBypassNominations(check.id, { bypass_skipped: nextSkipped });
        return;
      }
      setLoading(true);
      await updateCheckRef.current(check.id, { details });
    } catch {
      setOptimisticBypassSkipped(priorSkipped || null);
      checkDetailsRef.current = priorDetails;
    } finally {
      skippingBypassRefereeRef.current = false;
      setLoading(false);
    }
  };

  const onSkipAllBypassReferees = () => {
    if (skippingBypassRefereeRef.current || markingBypassCompleteRef.current) return;
    const bypassSkipped = totalRefereeSlots - checkReferees.length;
    setOptimisticBypassSkipped(bypassSkipped);
    checkDetailsRef.current = { ...checkDetailsRef.current, bypass_skipped: bypassSkipped };
    finishBypassNominations(check.id, { bypass_skipped: bypassSkipped });
  };

  const addRefereeFooterComponent = () => {
    return (
      <div className="flex flex-col gap-2 border-t border-neutral-4 pt-8 pb-8">
        <span className="text-2xl font-medium text-neutral-7">What happens next?</span>
        <span className="text-2xl font-light text-text-tertiary">
          After creating the referee, you'll have the option to send the request now or skip
          notification for the moment.
        </span>
      </div>
    );
  };

  const isPendingNomination = referee =>
    !referee.submitted_at &&
    !referee.completed_at &&
    !referee.replaced_by &&
    !referee.canceled &&
    !referee.deleted;

  const createCard = referee => (
    <RefereeCard
      key={referee.id}
      name={referee.submitted_at ? referee.name : 'Pending candidate nomination'}
      subtitle={
        <div className="d-flex align-items-center u-gap-small">
          <IconComponent icon={LicenseDraftIcon} noBackground size="x-small" type="secondary" />
          <span className="u-font-size-x-small u-font-weight-400 secondary-text">
            {referee.form_name}
          </span>
        </div>
      }
      referee={referee}
      flagCounts={refereeFlagCounts(flagCounts, referee)}
      selected={selectedReferee?.id === referee.id}
      disabled={referee.replaced_by || !referee.submitted_at}
      onClick={setSelectedReferee}
      referees={referees}
      onDelete={isPendingNomination(referee) ? setRefereeToDelete : undefined}
    />
  );

  const handleDeletePendingNomination = async () => {
    const refereeId = refereeToDelete.id;
    setRefereeToDelete(null);
    if (selectedReferee?.id === refereeId) setSelectedReferee(null);
    setLoading(true);
    await deleteReferee(refereeId);
    setLoading(false);
  };

  const primaryCards = referees.filter(r => !r.backup).map(createCard);
  const backupCards = referees.filter(r => r.backup).map(createCard);

  const renderRefereeCards = (referees, allSameForm) => {
    const completedReferees = referees.filter(
      referee => !referee.replaced_by && !referee.canceled && referee.completed_at,
    );

    return (
      <div className="flex flex-col gap-8 w-1/4">
        {allSameForm && completedReferees.length > 1 && (
          <CombinedRefereeCard
            completedReferees={completedReferees}
            selectedReferee={selectedReferee}
            onSelect={setSelectedReferee}
          />
        )}
        <div className="flex flex-col gap-8">
          <p className="text-lg font-medium text-text-tertiary uppercase tracking-wide">
            Primary Referees
          </p>
          {primaryCards}
        </div>

        <AddRefereeCard onClick={() => setShowNewRefereeModal(true)} />

        {backupCards.length > 0 && <BackupRefereesSection backupCards={backupCards} />}
      </div>
    );
  };

  const clearAddRefereeParam = () => {
    const params = new URLSearchParams(location.search);
    params.delete('addReferee');
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
      replace: true,
    });
  };

  const persistDocumentSelection = async documentId => {
    if (documentId === (checkDetailsRef.current?.referee_document_id ?? null)) return;
    const details = { ...checkDetailsRef.current, referee_document_id: documentId };
    checkDetailsRef.current = details;
    await updateCheckRef.current(check.id, { details });
  };

  const handleNewRefereeFormSelection = async data => {
    await persistDocumentSelection(data.referee_document_id ?? null);
    switch (data.input_type) {
      case 'enter_details':
        setShowNewRefereeModal(false);
        clearAddRefereeParam();
        if (showBypassForm) break;
        setRefereeFormId(data.referee_form_id);
        const selectedCandidateForm = candidateForms.find(
          form => form.id === data.candidate_form_id,
        );
        setCandidateForm(selectedCandidateForm);
        break;
      case 'ask_candidate':
        setLoading(true);
        try {
          await addReferee({
            candidate_id: candidate.id,
            candidate_check_id: check.id,
            form_id: data.referee_form_id,
            candidate_form_id: data.candidate_form_id,
            manual: false,
            template_id: data.template_id,
          });
          setShowNewRefereeModal(false);
          setSelectedReferee(null);
          clearAddRefereeParam();
        } finally {
          setLoading(false);
        }
        break;
    }
  };

  const onNewReferee = async answers => {
    setLoading(true);
    const data = {
      candidate_id: candidate.id,
      candidate_check_id: check.id,
      candidate_form_id: candidateForm.id,
      form_id: refereeFormId,
      manual: true,
      answers,
    };
    try {
      const referee = await addReferee(data);
      setSelectedReferee(referee);
      setCandidateForm(null);
      setShowRequestAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRefereeRequestEmail = async templateId => {
    setShowRefereeRequestEmailModal(false);
    await sendRequestEmail(selectedReferee.id, templateId);
  };

  const getBrandRefereeEmailTemplateId = useCallback(() => {
    return brand?.email_active
      ? (brand?.email_template_links?.new_request_to_referee ?? null)
      : null;
  }, [brand]);

  return (
    <div className={cn(styles.root, 'bg-neutral-2 rounded-2xl border border-border-primary')}>
      <NewRefereeModal
        brand={brand}
        visible={showNewRefereeModal}
        candidateForms={candidateForms}
        selectedCandidateForm={isSameCandidateFormsUsed ? userSelectedCandidateFormId : null}
        selectedRefereeForm={isSameRefereeFormsUsed ? userSelectedRefereeFormId : null}
        refereeForms={refereeForms?.filter(form => !form.archived)}
        documents={refereeDocuments}
        selectedDocumentId={refereeDocumentId}
        onSelect={handleNewRefereeFormSelection}
        onClose={() => {
          setShowNewRefereeModal(false);
          clearAddRefereeParam();
        }}
      />
      <div className="flex flex-col gap-1 p-8  bg-neutral-1 rounded-t-2xl border-b border-border-primary">
        <div className="text-text-primary text-[18px] font-semibold">Referees</div>
        <div className="text-text-tertiary text-[14px] font-light">
          {referees?.length > 0
            ? 'View and update the list of referees provided by the candidate'
            : 'No referees have been provided'}
        </div>
      </div>
      <div className="flex flex-wrap gap-8 p-8">
        {renderRefereeCards(
          referees,
          referees.every(referee => referee['form_id'] === referees[0]['form_id']),
        )}
        {!showBypassForm && candidateForm && (
          <div className="bg-neutral-1 rounded-3xl border border-neutral-4 w-1/2">
            <div className="flex items-center p-8 border-b border-neutral-4 justify-between">
              <div className="flex items-center gap-2">
                <IconComponent icon={UserAdd01Icon} />
                <div className="flex flex-col gap-1">
                  <span className="text-3xl font-medium text-neutral-7">Add referee details</span>
                  <span className="text-xl font-medium text-neutral-6">{candidateForm?.name}</span>
                </div>
              </div>
            </div>
            <div className="p-8">
              <Form
                candidateId={candidate.id}
                country={company.country}
                textParams={{
                  candidate: candidate.first_name,
                  candidateFirstName: candidate.first_name,
                  candidateFullName: candidate.name,
                  candidateLastName: candidate.last_name,
                  job: candidate.job_role,
                  companyName: brand?.brand_name ?? company?.name,
                }}
                form={candidateForm}
                brand={brand}
                buttonTitle="Add referee"
                footerComponent={addRefereeFooterComponent()}
                cancelAction={() => setCandidateForm(null)}
                cancelActionTitle="Cancel nomination"
                onSubmit={onNewReferee}
                getCandidateUpload={getUpload}
                deleteCandidateUpload={deleteUpload}
                signCandidateUpload={signUpload}
                createCandidateUpload={createUpload}
              />
            </div>
          </div>
        )}
        {showBypassForm && currentBypassForm && (
          <div className="bg-neutral-1 rounded-3xl border border-neutral-4 w-1/2">
            <div className="flex items-center p-8 border-b border-neutral-4 justify-between">
              <div className="flex items-center gap-2">
                <IconComponent icon={UserAdd01Icon} />
                <div className="flex flex-col gap-1">
                  <span className="text-3xl font-medium text-neutral-7">Add referee details</span>
                  <span className="text-xl font-medium text-neutral-6">
                    {`Referee ${accountedBypassSlots + 1} of ${totalRefereeSlots}: ${currentBypassForm.candidateForm.name}`}
                  </span>
                </div>
              </div>
              {remainingBypassSlots > 1 && (
                <Button type="secondary" small onClick={onSkipAllBypassReferees}>
                  Skip all
                </Button>
              )}
            </div>
            <div className="p-8">
              <Form
                key={accountedBypassSlots}
                ref={bypassFormRef}
                candidateId={candidate.id}
                country={company.country}
                textParams={{
                  candidate: candidate.first_name,
                  candidateFirstName: candidate.first_name,
                  candidateFullName: candidate.name,
                  candidateLastName: candidate.last_name,
                  job: candidate.job_role,
                  companyName: brand?.brand_name ?? company?.name,
                }}
                form={currentBypassForm.candidateForm}
                brand={brand}
                buttonTitle="Add referee"
                footerComponent={addRefereeFooterComponent()}
                cancelAction={onSkipBypassReferee}
                cancelActionTitle="Skip this referee"
                onSubmit={onBypassRefereeSubmit}
                getCandidateUpload={getUpload}
                deleteCandidateUpload={deleteUpload}
                signCandidateUpload={signUpload}
                createCandidateUpload={createUpload}
              />
            </div>
          </div>
        )}
        {!showBypassForm && selectedReferee?.id === COMBINED_ID && !candidateForm && (
          <CombinedReferees
            candidate={candidate}
            check={check}
            brand={brand}
            canAccessResults={canAccessResults}
            referees={referees.filter(
              referee => !referee.replaced_by && !referee.canceled && referee.completed_at,
            )}
            setLoading={setLoading}
            messagePopUp={messagePopUp}
            getPdfReport={getCombinedReport}
            getCustomPdfReport={getCombinedCustomReport}
            onClose={() => setSelectedReferee(null)}
          />
        )}

        {!showBypassForm &&
          selectedReferee &&
          selectedReferee.id !== COMBINED_ID &&
          !candidateForm && (
            <Referee
              brand={brand}
              candidate={candidate}
              company={company}
              check={check}
              referees={referees}
              referee={selectedReferee}
              canAccessResults={canAccessResults}
              setLoading={setLoading}
              messagePopUp={messagePopUp}
              updateReferee={updateReferee}
              deleteReferee={deleteReferee}
              cancelReferee={cancelReferee}
              getPdfReport={getPdfReport}
              getCustomPdfReport={getCustomPdfReport}
              setShowRequestAlert={() => setShowRequestAlert(true)}
              onNewRequest={() => setShowRefereeRequestEmailModal(true)}
              sendReplacementRequest={sendReplacementRequest}
              onClose={() => setSelectedReferee(null)}
            />
          )}
      </div>
      <Alert
        show={showRequestAlert}
        title="Would you like to send a new request to the referee?"
        ok="Send New Request"
        cancel="No Thanks"
        params={showRequestAlert}
        onOk={() => {
          setShowRequestAlert(false);
          setShowRefereeRequestEmailModal(true);
        }}
        onCancel={() => setShowRequestAlert(false)}
        okTestId="send-new-request-alert-ok-button"
      />
      <Alert
        show={!!refereeToDelete}
        title="Delete this pending referee nomination?"
        message="The candidate will no longer be asked to nominate this referee and this cannot be undone. Once deleted, we will adjust the number of referees for this candidate."
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        showConfirmationInput
        cancel="Cancel"
        onOk={handleDeletePendingNomination}
        onCancel={() => setRefereeToDelete(null)}
      />
      <SendRefereeRequestModal
        brand={brand}
        referee={selectedReferee}
        templateIdInBrandDefault={getBrandRefereeEmailTemplateId()}
        visible={showRefereeRequestEmailModal}
        onClose={() => setShowRefereeRequestEmailModal(false)}
        onSend={handleSendRefereeRequestEmail}
      />
    </div>
  );
};

const CombinedRefereeCard = ({ completedReferees, selectedReferee, onSelect }) => (
  <div>
    <RefereeCard
      combined
      title="Combined Report"
      name={completedReferees.map(referee => referee.name).join(' & ')}
      referee={{ id: COMBINED_ID }}
      onClick={onSelect}
      selected={selectedReferee?.id === COMBINED_ID}
    />
  </div>
);

const BackupRefereesSection = ({ backupCards }) => (
  <>
    <div className="u-divider" />
    <p>Backup Referees</p>
    {backupCards}
  </>
);
